import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Shield, Star, Trash2, UserPlus, History, ArrowLeft } from 'lucide-react';

/**
 * Iter-300 — AdminCompanySignersPage
 *
 * Single-page UI for the Iter-298/299 backend that already exists:
 *   • GET    /api/contracts/admin/companies/{id}/signers
 *   • POST   /api/contracts/admin/companies/{id}/signers
 *   • DELETE /api/contracts/admin/companies/{id}/signers/{user_id}
 *   • GET    /api/contracts/company/{id}/signature
 *
 * Mounted at `/admin/companies/:companyId/signers`. Provides:
 *   • Active + revoked signer table.
 *   • Promote a signer to Primary (re-POSTs with is_primary=true).
 *   • Revoke a signer (DELETE).
 *   • Add a signer (POST — admin pastes the user_id; future: typeahead).
 *   • Agreement audit timeline (every company_agreements row).
 */
const AdminCompanySignersPage = () => {
  const { companyId } = useParams();
  const [signers, setSigners] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newSignerId, setNewSignerId] = useState('');
  const [newSignerTitle, setNewSignerTitle] = useState('');
  const [newSignerPrimary, setNewSignerPrimary] = useState(false);
  const [companyName, setCompanyName] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sigResp, agResp] = await Promise.all([
        api.get(`/contracts/admin/companies/${companyId}/signers`),
        api.get(`/contracts/company/${companyId}/signature`),
      ]);
      setSigners(sigResp.data.signers || []);
      setAgreements(agResp.data.agreements || []);
      const latest = (agResp.data.agreements || [])[0];
      if (latest?.company_name) setCompanyName(latest.company_name);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load company signers');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleAddSigner = async () => {
    if (!newSignerId.trim()) {
      toast.error('User ID is required');
      return;
    }
    setAdding(true);
    try {
      await api.post(`/contracts/admin/companies/${companyId}/signers`, {
        user_id: newSignerId.trim(),
        signer_title: newSignerTitle.trim() || null,
        is_primary: newSignerPrimary,
      });
      toast.success('Signer added');
      setNewSignerId('');
      setNewSignerTitle('');
      setNewSignerPrimary(false);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add signer');
    } finally {
      setAdding(false);
    }
  };

  const handleMakePrimary = async (signer) => {
    try {
      await api.post(`/contracts/admin/companies/${companyId}/signers`, {
        user_id: signer.user_id,
        signer_title: signer.signer_title,
        is_primary: true,
      });
      toast.success(`Promoted ${signer.name || signer.email} to Primary`);
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to promote signer');
    }
  };

  const handleRevoke = async (signer) => {
    if (!window.confirm(`Revoke ${signer.name || signer.email}? They will no longer be allowed to sign or re-sign on behalf of this company.`)) return;
    try {
      await api.delete(`/contracts/admin/companies/${companyId}/signers/${signer.user_id}`);
      toast.success('Signer revoked');
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to revoke signer');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8" data-testid="admin-company-signers-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700">
              <ArrowLeft className="w-3.5 h-3.5" /> Admin
            </Link>
            <h1 className="text-2xl font-semibold text-slate-900 mt-1 flex items-center gap-2">
              <Shield className="w-6 h-6 text-emerald-600" />
              Authorized Signers — {companyName || 'Company'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">Manage who is allowed to execute or re-sign the iEvntür Service Agreement on behalf of this company.</p>
          </div>
        </div>

        {/* Signer table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base text-slate-900">Authorized Signers</CardTitle>
            <Badge variant="outline" data-testid="signer-count">{signers.filter((s) => s.is_active).length} active · {signers.length} total</Badge>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-slate-500 text-sm">Loading…</div>
            ) : signers.length === 0 ? (
              <div className="text-slate-500 text-sm" data-testid="signers-empty">No authorized signers on file. Add one below — they will be set as Primary automatically.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500 border-b border-slate-200">
                  <tr>
                    <th className="py-2">Signer</th>
                    <th className="py-2">Title</th>
                    <th className="py-2">Added</th>
                    <th className="py-2">Status</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {signers.map((s) => (
                    <tr key={s.id} className="border-b border-slate-100" data-testid={`signer-row-${s.user_id}`}>
                      <td className="py-3">
                        <div className="text-slate-900 font-medium flex items-center gap-1">
                          {s.is_primary && s.is_active && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                          {s.name || s.email}
                        </div>
                        <div className="text-xs text-slate-500">{s.email}</div>
                      </td>
                      <td className="py-3 text-slate-700">{s.signer_title || '—'}</td>
                      <td className="py-3 text-slate-500 text-xs">{s.added_at ? new Date(s.added_at).toLocaleDateString() : '—'}</td>
                      <td className="py-3">
                        {!s.is_active ? (
                          <Badge variant="destructive">Revoked</Badge>
                        ) : s.is_primary ? (
                          <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-200">Primary</Badge>
                        ) : (
                          <Badge variant="secondary">Authorized</Badge>
                        )}
                      </td>
                      <td className="py-3 text-right space-x-2">
                        {s.is_active && !s.is_primary && (
                          <Button
                            size="sm" variant="outline"
                            onClick={() => handleMakePrimary(s)}
                            data-testid={`make-primary-${s.user_id}`}
                          >
                            <Star className="w-3.5 h-3.5 mr-1" /> Make Primary
                          </Button>
                        )}
                        {s.is_active && (
                          <Button
                            size="sm" variant="ghost" className="text-rose-600 hover:bg-rose-50"
                            onClick={() => handleRevoke(s)}
                            data-testid={`revoke-signer-${s.user_id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 mr-1" /> Revoke
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Add signer */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-slate-900 flex items-center gap-2">
              <UserPlus className="w-4 h-4 text-emerald-600" /> Add Authorized Signer
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-slate-500">User must already have an iEvntür account at this company. Paste the user ID below — future versions will provide a typeahead picker.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-600">User ID</Label>
                <Input
                  value={newSignerId}
                  onChange={(e) => setNewSignerId(e.target.value)}
                  placeholder="uuid"
                  data-testid="add-signer-user-id"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Title (optional)</Label>
                <Input
                  value={newSignerTitle}
                  onChange={(e) => setNewSignerTitle(e.target.value)}
                  placeholder="VP, Marketing"
                  data-testid="add-signer-title"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newSignerPrimary}
                    onChange={(e) => setNewSignerPrimary(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="add-signer-primary"
                  />
                  Set as Primary
                </label>
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={handleAddSigner}
                disabled={adding || !newSignerId.trim()}
                data-testid="add-signer-submit"
              >
                {adding ? 'Adding…' : 'Add Signer'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Agreement audit */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-slate-900 flex items-center gap-2">
              <History className="w-4 h-4 text-slate-500" /> Agreement Audit Trail
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agreements.length === 0 ? (
              <div className="text-slate-500 text-sm" data-testid="agreements-empty">No signed agreements on file for this company yet.</div>
            ) : (
              <div className="space-y-3">
                {agreements.map((a) => (
                  <div key={a.id} className="rounded-lg border border-slate-200 bg-white p-3 text-sm" data-testid={`agreement-row-${a.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium text-slate-900">{a.contract_title} — V{a.contract_version}</div>
                      {a.revoked_at ? (
                        <Badge variant="destructive">Revoked</Badge>
                      ) : a.authority_certified ? (
                        <Badge className="bg-emerald-100 text-emerald-900 hover:bg-emerald-200">Authority Certified</Badge>
                      ) : (
                        <Badge variant="secondary">Legacy signature</Badge>
                      )}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">
                      Signed by <strong>{a.signer_name}</strong>{a.signer_title ? ` (${a.signer_title})` : ''} · {a.signer_email}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {a.signed_at ? new Date(a.signed_at).toLocaleString() : '—'} · IP {a.ip_address || '—'}
                      {a.re_sign_reason ? ` · Re-sign reason: ${a.re_sign_reason}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminCompanySignersPage;
